import { SpaceType } from './spaceTime.types'
import {
  Alert,
  AlertPresetBase,
  AlertPresetOutcome,
  AlertType,
  Density,
  InsightAlertOutcome,
} from './variables.typing'

export const spaceTypeSingularLabels = {
  item: 'Item',
  neighborhood: 'Bairro',
  district: 'Distrito',
  street: 'Rua',
  city: 'Município',
  microRegion: 'Microrregião',
  mesoRegion: 'Mesorregião',
  state: 'Estado',
  macroRegion: 'Macrorregião',
  country: 'País',
  custom: 'Personalizado',
}

export const spaceTypeGrammaticalGender = {
  item: 'male',
  neighborhood: 'male',
  district: 'male',
  street: 'female',
  city: 'male',
  microRegion: 'female',
  mesoRegion: 'female',
  state: 'male',
  macroRegion: 'female',
  country: 'male',
  custom: 'male',
} as const

export const compatibleSpaceChildren: Record<SpaceType, SpaceType[]> = {
  custom: ['item'],
  country: ['state', 'macroRegion', 'mesoRegion', 'microRegion', 'item', 'country'],
  macroRegion: ['state', 'mesoRegion', 'microRegion', 'item', 'macroRegion'],
  state: ['city', 'mesoRegion', 'microRegion', 'item', 'state'],
  mesoRegion: ['city', 'microRegion', 'item', 'mesoRegion'],
  microRegion: ['city', 'neighborhood', 'item', 'microRegion'],
  city: ['neighborhood', 'street', 'item', 'city'],
  neighborhood: ['street', 'item', 'neighborhood'],
  street: ['item', 'street'],
  item: [],
}

export const spaceTypePluralLabels = {
  item: 'Itens',
  neighborhood: 'Bairros',
  district: 'Distritos',
  street: 'Ruas',
  city: 'Municípios',
  microRegion: 'Microrregiões',
  mesoRegion: 'Mesorregiões',
  state: 'Estados',
  macroRegion: 'Macrorregiões',
  country: 'Países',
  custom: 'Personalizado',
}

export const childrenItemViewLabels = {
  heatmap: 'Mapa de calor',
  cluster: 'Agrupamentos',
  arc: 'Arcos',
}

type AlertLike = Alert | AlertType | null | undefined
type AlertDensityContext = { betterDensity?: Density | null } | null | undefined

type AlertPresetConfig = {
  label: string
  legacyColor: string
  legacyTextColor?: string
  direction?: 'increase' | 'decrease'
  defaultOutcome: AlertPresetOutcome
  relevance?: number
  goodColor: string
  badColor: string
  goodTextColor?: string
  badTextColor?: string
}

type AlertMetadata = {
  base: AlertPresetBase
  label: string
  color: string
  textColor: string
  relevance?: number
  qualifiedOutcome?: AlertPresetOutcome
}

export const ALERT_PRESET_BASES = [
  'notification',
  'outbreak',
  'urgent',
  'anomaly',
  'emergent',
  'new',
  'decline',
  'important',
  'informative',
  'normal',
  'increase',
  'decrease',
  'sparse',
] as const satisfies readonly AlertPresetBase[]

export const ALERT_PRESET_OUTCOMES = [
  'good',
  'bad',
] as const satisfies readonly AlertPresetOutcome[]

const alertPresetConfig: Record<AlertPresetBase, AlertPresetConfig> = {
  notification: {
    label: 'Agravo',
    legacyColor: '#000000',
    defaultOutcome: 'bad',
    goodColor: '#0F766E',
    badColor: '#000000',
  },
  outbreak: {
    label: 'Anomalia',
    legacyColor: '#5f27cd',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.2,
    goodColor: '#0891B2',
    badColor: '#5f27cd',
  },
  urgent: {
    label: 'Crescimento abrupto',
    legacyColor: '#005c0b',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.4,
    goodColor: '#15803D',
    badColor: '#c42210',
  },
  anomaly: {
    label: 'Anomalia',
    legacyColor: '#5f27cd',
    defaultOutcome: 'bad',
    goodColor: '#0284C7',
    badColor: '#5f27cd',
  },
  emergent: {
    label: 'Emergente',
    legacyColor: '#0dc022',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.6,
    goodColor: '#16A34A',
    badColor: '#DC2626',
  },
  new: {
    label: 'Novo',
    legacyColor: '#16a085',
    direction: 'increase',
    defaultOutcome: 'bad',
    goodColor: '#0F766E',
    badColor: '#DC2626',
  },
  decline: {
    label: 'Declínio',
    legacyColor: '#c42210',
    direction: 'decrease',
    defaultOutcome: 'bad',
    relevance: 0.9,
    goodColor: '#0F766E',
    badColor: '#c42210',
  },
  important: {
    label: 'Importante',
    legacyColor: '#FF9800',
    defaultOutcome: 'bad',
    goodColor: '#2563EB',
    badColor: '#FF9800',
  },
  informative: {
    label: 'Informações',
    legacyColor: '#00BCD4',
    defaultOutcome: 'good',
    goodColor: '#0284C7',
    badColor: '#00BCD4',
  },
  normal: {
    label: 'Normal',
    legacyColor: '#E5E7EB',
    legacyTextColor: '#374151',
    defaultOutcome: 'good',
    goodColor: '#E5E7EB',
    badColor: '#D1D5DB',
    goodTextColor: '#374151',
    badTextColor: '#374151',
  },
  increase: {
    label: 'Aumento',
    legacyColor: '#2ecc71',
    direction: 'increase',
    defaultOutcome: 'bad',
    goodColor: '#16A34A',
    badColor: '#e74c3c',
  },
  decrease: {
    label: 'Diminuição',
    legacyColor: '#e74c3c',
    direction: 'decrease',
    defaultOutcome: 'bad',
    goodColor: '#0F766E',
    badColor: '#EF4444',
  },
  sparse: {
    label: 'Poucos dados',
    legacyColor: '#7f8c8d',
    defaultOutcome: 'bad',
    goodColor: '#0F766E',
    badColor: '#7f8c8d',
  },
}

function capitalizeAlertBase(base: AlertPresetBase): Capitalize<AlertPresetBase> {
  return `${base.charAt(0).toUpperCase()}${base.slice(1)}` as Capitalize<AlertPresetBase>
}

export function toAlertType(outcome: AlertPresetOutcome, base: AlertPresetBase): AlertType {
  return `${outcome}${capitalizeAlertBase(base)}` as AlertType
}

const qualifiedAlertMetadata = Object.fromEntries(
  ALERT_PRESET_BASES.flatMap(base =>
    ALERT_PRESET_OUTCOMES.map(outcome => {
      const config = alertPresetConfig[base]
      return [
        toAlertType(outcome, base),
        {
          base,
          label: config.label,
          color: outcome === 'good' ? config.goodColor : config.badColor,
          textColor:
            outcome === 'good'
              ? (config.goodTextColor ?? '#FFFFFF')
              : (config.badTextColor ?? '#FFFFFF'),
          relevance: config.relevance,
          qualifiedOutcome: outcome,
        } satisfies AlertMetadata,
      ]
    }),
  ),
) as Record<Exclude<AlertType, AlertPresetBase>, AlertMetadata>

const legacyAlertMetadata = Object.fromEntries(
  ALERT_PRESET_BASES.map(base => {
    const config = alertPresetConfig[base]
    return [
      base,
      {
        base,
        label: config.label,
        color: config.legacyColor,
        textColor: config.legacyTextColor ?? '#FFFFFF',
        relevance: config.relevance,
      } satisfies AlertMetadata,
    ]
  }),
) as Record<AlertPresetBase, AlertMetadata>

const alertMetadata: Record<AlertType, AlertMetadata> = {
  ...qualifiedAlertMetadata,
  ...legacyAlertMetadata,
}

export const alertLabels = Object.fromEntries(
  Object.entries(alertMetadata).map(([alertType, metadata]) => [alertType, metadata.label]),
) as Record<AlertType, string>

export const alertColors = Object.fromEntries(
  Object.entries(alertMetadata).map(([alertType, metadata]) => [alertType, metadata.color]),
) as Record<AlertType, string>

export const alertTextColors = Object.fromEntries(
  Object.entries(alertMetadata).map(([alertType, metadata]) => [alertType, metadata.textColor]),
) as Record<AlertType, string>

export const alertRelevance = Object.fromEntries(
  Object.entries(alertMetadata)
    .filter(([, metadata]) => typeof metadata.relevance === 'number')
    .map(([alertType, metadata]) => [alertType, metadata.relevance]),
) as Partial<Record<AlertType, number>>

export function isAlertType(value: unknown): value is AlertType {
  return typeof value === 'string' && value in alertMetadata
}

export function getAlertBaseFromType(alertType: AlertType): AlertPresetBase {
  return alertMetadata[alertType].base
}

export function getAlertOutcomeFromType(alertType: AlertType): InsightAlertOutcome {
  if (alertType === 'normal') {
    return 'normal'
  }

  const qualifiedOutcome = alertMetadata[alertType].qualifiedOutcome
  if (qualifiedOutcome === 'good' || qualifiedOutcome === 'bad') {
    return qualifiedOutcome
  }

  return alertPresetConfig[getAlertBaseFromType(alertType)].defaultOutcome
}

export function resolveAlertOutcomeForVariable(
  base: AlertPresetBase,
  variable?: AlertDensityContext,
): AlertPresetOutcome {
  const config = alertPresetConfig[base]

  if (config.direction === 'increase') {
    if (variable?.betterDensity === 'more') return 'good'
    if (variable?.betterDensity === 'less') return 'bad'
  }

  if (config.direction === 'decrease') {
    if (variable?.betterDensity === 'less') return 'good'
    if (variable?.betterDensity === 'more') return 'bad'
  }

  return config.defaultOutcome
}

export function resolveAlertTypeForVariable(
  base: AlertPresetBase,
  variable?: AlertDensityContext,
  outcome?: AlertPresetOutcome,
): AlertType {
  return toAlertType(outcome ?? resolveAlertOutcomeForVariable(base, variable), base)
}

export function getAlertOutcomeFromAlert(
  alert: AlertLike,
  fallback?: InsightAlertOutcome,
): InsightAlertOutcome {
  if (fallback === 'normal' || fallback === 'good' || fallback === 'bad') {
    return fallback
  }

  if (!alert) {
    return 'normal'
  }

  if (typeof alert === 'string' && isAlertType(alert)) {
    return getAlertOutcomeFromType(alert)
  }

  if (
    typeof alert === 'object' &&
    'preset' in alert &&
    typeof alert.preset === 'string' &&
    isAlertType(alert.preset)
  ) {
    return getAlertOutcomeFromType(alert.preset)
  }

  return fallback ?? 'bad'
}

function isPresetAlert(alert: AlertLike): alert is Extract<Alert, { preset: AlertType }> {
  return alert !== null && alert !== undefined && typeof alert === 'object' && 'preset' in alert
}

function isCustomAlert(alert: AlertLike): alert is Extract<Alert, { color: string }> {
  return (
    alert !== null &&
    alert !== undefined &&
    typeof alert === 'object' &&
    !('preset' in alert) &&
    'color' in alert
  )
}

function resolvePreset(alert: AlertLike): AlertType | null {
  if (!alert) {
    return null
  }

  if (typeof alert === 'string') {
    return isAlertType(alert) ? alert : null
  }

  if (isPresetAlert(alert)) {
    return isAlertType(alert.preset) ? alert.preset : null
  }

  return null
}

export function getAlertLabel(alert: AlertLike) {
  if (!alert) {
    return ''
  }

  if (isCustomAlert(alert)) {
    return alert.name
  }

  const preset = resolvePreset(alert)
  return (preset && alertLabels[preset]) || ''
}

export function getAlertColor(alert: AlertLike) {
  if (isCustomAlert(alert)) {
    return alert.color
  }

  const preset = resolvePreset(alert)
  return (preset && alertColors[preset]) || alertColors.normal
}

export function getAlertTextColor(alert: AlertLike) {
  if (isCustomAlert(alert)) {
    return '#FFFFFF'
  }

  const preset = resolvePreset(alert)
  return (preset && alertTextColors[preset]) || '#FFFFFF'
}
